// Professional Animation Utilities for Global Solar Website

// Intersection Observer for scroll animations
const observerOptions = {
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
};

// Initialize scroll animations
export const initScrollAnimations = () => {
  // Create intersection observer
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        
        // Add animation class based on data attribute
        const animationType = element.dataset.animation || 'fade-in-up';
        element.classList.add(`animate-${animationType}`);
        
        // Add stagger delay for child elements
        const children = element.querySelectorAll('[data-stagger]');
        children.forEach((child, index) => {
          child.style.animationDelay = `${index * 0.1}s`;
          child.classList.add(`animate-${child.dataset.stagger || 'fade-in-up'}`);
        });
        
        // Unobserve after animation
        observer.unobserve(element);
      }
    });
  }, observerOptions);

  // Observe all elements with animation data attributes
  const animatedElements = document.querySelectorAll('[data-animation]');
  animatedElements.forEach(el => {
    observer.observe(el);
  });
};

// Smooth scroll to element
export const smoothScrollTo = (elementId, offset = 80) => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

// Add hover animations to cards
export const initHoverAnimations = () => {
  // Service cards
  const serviceCards = document.querySelectorAll('.service-card');
  serviceCards.forEach(card => {
    card.addEventListener('mouseenter', () => {
      card.style.transform = 'translateY(-8px) scale(1.02)';
    });
    
    card.addEventListener('mouseleave', () => {
      card.style.transform = 'translateY(0) scale(1)';
    });
  });

  // Counter cards
  const counterCards = document.querySelectorAll('.counter-card');
  counterCards.forEach(card => {
    card.addEventListener('mouseenter', () => {
      const number = card.querySelector('.counter-number');
      if (number) {
        number.style.transform = 'scale(1.1)';
        number.style.color = 'var(--accent-color)';
      }
    });
    
    card.addEventListener('mouseleave', () => {
      const number = card.querySelector('.counter-number');
      if (number) {
        number.style.transform = 'scale(1)';
        number.style.color = '';
      }
    });
  });
};

// Initialize all animations
export const initAllAnimations = () => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initScrollAnimations();
      initHoverAnimations();
    });
  } else {
    initScrollAnimations();
    initHoverAnimations();
  }
};

// Counter animation
export const animateCounter = (element, target, duration = 2000) => {
  const start = 0;
  const increment = target / (duration / 16);
  let current = start;
  
  const timer = setInterval(() => {
    current += increment;
    if (current >= target) {
      current = target;
      clearInterval(timer);
    }
    
    // Format number with + suffix if it exists in original
    const hasPlus = element.textContent.includes('+');
    const hasMW = element.textContent.includes('MW');
    
    let displayValue = Math.floor(current);
    if (hasMW) {
      displayValue = displayValue + 'MW';
    }
    if (hasPlus) {
      displayValue = displayValue + '+';
    }
    
    element.textContent = displayValue;
  }, 16);
};

// Initialize counter animations when visible
export const initCounterAnimations = () => {
  const counterNumbers = document.querySelectorAll('.counter-number');
  
  const counterObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const text = element.textContent;
        const number = parseInt(text.replace(/[^0-9]/g, ''));
        
        if (number) {
          animateCounter(element, number);
          counterObserver.unobserve(element);
        }
      }
    });
  }, { threshold: 0.5 });
  
  counterNumbers.forEach(counter => {
    counterObserver.observe(counter);
  });
};

// Parallax effect for hero sections
export const initParallaxEffect = () => {
  const parallaxElements = document.querySelectorAll('[data-parallax]');
  
  window.addEventListener('scroll', () => {
    const scrolled = window.pageYOffset;
    
    parallaxElements.forEach(element => {
      const rate = scrolled * -0.5;
      element.style.transform = `translateY(${rate}px)`;
    });
  });
};

// Loading animation
export const showLoadingAnimation = () => {
  const loader = document.createElement('div');
  loader.className = 'loading-overlay';
  loader.innerHTML = `
    <div class="loading-spinner">
      <div class="spinner"></div>
      <p class="loading-text">Loading...</p>
    </div>
  `;
  document.body.appendChild(loader);
  
  return loader;
};

export const hideLoadingAnimation = (loader) => {
  if (loader) {
    loader.style.opacity = '0';
    setTimeout(() => {
      loader.remove();
    }, 300);
  }
};